import { useCallback, useEffect, useState } from 'react'
import {
  colors,
  Text,
  Snackbar as BsSnackbar,
  breakpoints,
  Button,
  AddIcon,
  WarningIcon,
  CloseIcon,
} from '@resiliatech/birdsong'
import styled from '@emotion/styled'
import { Alert, Box, Snackbar } from '@mui/material'
import { AddMembersDialog } from 'Components/Pages/Team/TeamPage/AddMemberDialog/AddMembersDialog'
import { RemoveMemberDialog } from 'Components/Pages/Team/TeamPage/AddMemberDialog/RemoveMemberDialog'
import { NewTeamPageTable } from 'Components/Pages/Team/NewTeamPageTable/NewTeamPageTable'
import { useAppState } from 'Context/AppContext/AppContext'
import { hasPermissions } from 'Components/SharedComponents/WithPermission/HasPermission'
import { useLocation } from 'react-router'
import {
  useGetFunderUsersQuery,
  useUpdateFunderUserSettingsMutation,
} from 'Generated/graphql-types'
import ErrorMessage from 'Components/SharedComponents/ErrorMessage'
import { SuperLoading } from 'Components/SharedComponents/Loading/SuperLoading'
import { FunderUser } from 'TypeDefs/Team'
import { analytics } from 'Modules/Analytics'
import { team } from 'Modules/Analytics/trackActionInterface/Account/team'
import { useStyles } from './styles'

const FunderTeamPage = () => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [snackBarErrorOpen, setSnackBarErrorOpen] = useState(false)

  const [deletedUser, setDeletedUser] = useState<{
    id: number
    email: string
    name?: string
  } | null>(null)

  const [showBirdsongSnackbar, setShowBirdsongSnackbar] = useState(false)
  const [birdsongSnackbarMessage, setBirdsongSnackbarMessage] = useState('')

  const {
    funderOrgConfig: { orgId: funderOrgId },
    userConfig,
  } = useAppState()

  const canAddTeamMember = hasPermissions(
    ['team:create'],
    userConfig.permissions
  )

  const { data, loading, error } = useGetFunderUsersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      funderOrgId,
    },
    skip: !funderOrgId,
  })

  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const showModal = params.get('showModal')
  const handleClick = useCallback(() => {
    const dispatchAddMemberClicked = analytics.trackActionCreator(
      team.Add_Member_Clicked
    )
    if (showModal !== 'addMember') {
      dispatchAddMemberClicked({})
    }
    setOpen(true)
  }, [showModal])

  useEffect(() => {
    if (showModal === 'addMember' && canAddTeamMember) {
      handleClick()
      window.history.replaceState({}, document.title)
    }
  }, [showModal, canAddTeamMember, handleClick])

  const [updateUserSettings] = useUpdateFunderUserSettingsMutation()

  if (error) {
    return (
      <Box sx={classes.pageWrapper}>
        <ErrorMessage />
      </Box>
    )
  }

  if (
    loading ||
    !data ||
    !data.getFunderUsers ||
    data.getFunderUsers.length === 0
  ) {
    return (
      <Box sx={classes.pageWrapper}>
        <SuperLoading />
      </Box>
    )
  }

  return (
    <Box sx={classes.pageWrapper}>
      <Box sx={classes.pageTitle}>
        <Box sx={classes.limitSection}>
          <Title>
            <Text variant='h1'>Team</Text>
          </Title>

          <Box sx={classes.button} display='flex'>
            <Button
              onClick={handleClick}
              variant='primary'
              label='Add Members'
              trailingIcon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={classes.tableWrapper}>
        <NewTeamPageTable
          users={data.getFunderUsers as FunderUser[]}
          updateUserSettings={updateUserSettings}
          setShowBirdsongSnackbar={setShowBirdsongSnackbar}
          setBirdsongSnackbarMessage={setBirdsongSnackbarMessage}
          setDeletedUser={setDeletedUser}
          isFunder
        />
      </Box>
      <StyledSnackbar
        open={showBirdsongSnackbar}
        onClose={() => setShowBirdsongSnackbar(false)}
        message={birdsongSnackbarMessage}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        autoHideDuration={6000}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarErrorOpen}
        onClose={() => setSnackBarErrorOpen(false)}
      >
        <Alert
          severity='warning'
          variant='filled'
          sx={{ ...classes.alert, ...classes.alertError }}
          icon={<WarningIcon />}
          onClose={() => setSnackBarErrorOpen(false)}
          action={
            <Box>
              <Button
                variant='contained'
                sx={classes.retryButton}
                onClick={() => setSnackBarErrorOpen(false)}
              >
                <CloseIcon />
              </Button>
            </Box>
          }
        >
          Please try again later.
        </Alert>
      </Snackbar>
      <AddMembersDialog
        open={open}
        setOpen={setOpen}
        setShowBirdsongSnackbar={setShowBirdsongSnackbar}
        setBirdsongSnackbarMessage={setBirdsongSnackbarMessage}
        isFunder
      />
      <RemoveMemberDialog
        open={deletedUser !== null}
        deletedUser={deletedUser}
        setDeletedUser={setDeletedUser}
        setShowBirdsongSnackbar={setShowBirdsongSnackbar}
        setBirdsongSnackbarMessage={setBirdsongSnackbarMessage}
        isFunder
      />
    </Box>
  )
}

export default FunderTeamPage

const Title = styled.div`
  color: ${colors.copy.headerDark};
`

const StyledSnackbar = styled(BsSnackbar)`
  @media screen and (min-width: ${breakpoints.medium}) {
    margin-left: 240px;
  }
`
